import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Input, Label } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './PersonalDetailsCheck.module.css';
import PersonalDetailsCheckErrors from './PersonalDetailsCheckErrors/PersonalDetailsCheckErrors';
import {
  CREDIT_DATA_REQUIREMENT_ERRORS,
  DIRECTOR_DATA_REQUIREMENT_ERRORS,
  EMPTY_ADDRESS,
} from '../../../../constants';
import { useContextStateKey } from '../../../../hooks/useContextStateKey';
import { useOffersUK } from '../../../../hooks/useOffers/useOffersUK';
import { useSignupRouterUK } from '../../../../hooks/useSignupRouter/useSignupRouterUK';
import {
  useGetSelectStateQuery,
  useGetStateQuery,
  useSplitTestBranch,
} from '../../../../lendingApi/lendingApiQueries';
import { IwQueryClient } from '../../../../store/IwQueryClient';
import { TGetStateResponse, TSignupStepsUK } from '../../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../../utils/amplitudeEvents';
import logger from '../../../../utils/logger';
import { Button } from '../../../Button/Button';
import {
  denormalisePersonalDataUK,
  normaliseStepData,
} from '../../../helpers/stateNormalisation';
import { APPLICANT_STEP_ID } from '../../../helpers/steps';
import AccountCreatedModal from '../../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';
import { AddressInput } from '../AddressInput/AddressInput';
import { DirectorSearch } from '../DirectorSearch/DirectorSearch';
import { TPersonalDetailsDataUK } from '../PersonalDetailsUK';
import personalDetailsStyles from '../PersonalDetailsUK.module.css';
import { schema } from '../PersonalDetailsUK.schema';

type TProps = {
  dataRequirementErrors: ReturnType<
    typeof useOffersUK
  >['dataRequirementErrors'];
  hasDocumentRequirements: ReturnType<
    typeof useOffersUK
  >['hasDocumentRequirements'];
  isRefetchingOffers: ReturnType<typeof useOffersUK>['isRefetchingOffers'];
  includeMiddleName?: boolean;
};

const PersonalDetailsCheck = ({
  dataRequirementErrors,
  hasDocumentRequirements,
  isRefetchingOffers,
  includeMiddleName,
}: TProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const signupNoPGTest = useSplitTestBranch('signup_no_pg_test', {
    enabled: true,
  });
  const { state, isStateFetching } = useGetStateQuery<TGetStateResponse>();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[APPLICANT_STEP_ID],
  );

  const hasDirectorDataRequirementError = () => {
    return dataRequirementErrors?.some((error) =>
      DIRECTOR_DATA_REQUIREMENT_ERRORS.includes(error),
    );
  };

  const hasCreditDataRequirementError = () => {
    return dataRequirementErrors?.some((error) =>
      CREDIT_DATA_REQUIREMENT_ERRORS.includes(error),
    );
  };

  const hasEquifaxTokenOnCurrentAddress = () => {
    return !!normalisedState.residentialAddresses?.[0]?.equifaxToken;
  };

  const normalisedStateWithoutErroredFields = () => {
    let filteredState = normalisedState;

    if (hasDirectorDataRequirementError()) {
      filteredState = {
        ...filteredState,
        firstName: undefined,
        lastName: undefined,
        dobDay: undefined,
        dobMonth: undefined,
        dobYear: undefined,
      };
    }

    if (hasCreditDataRequirementError()) {
      const allAddresses = filteredState.residentialAddresses;

      const updatedCurrentAddress = hasEquifaxTokenOnCurrentAddress()
        ? allAddresses[0]
        : EMPTY_ADDRESS;

      const updatedAllAddresses = [
        updatedCurrentAddress,
        ...allAddresses.slice(1),
      ];

      filteredState = {
        ...filteredState,
        residentialAddresses: updatedAllAddresses,
      };
    }
    return filteredState;
  };

  const { navigateSignup } = useSignupRouterUK();
  const stateKey = useContextStateKey();

  const methods = useForm<TPersonalDetailsDataUK>({
    resolver: yupResolver(schema),
    defaultValues: { ...normalisedStateWithoutErroredFields() },
  });

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Personal details check',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (values: TPersonalDetailsDataUK) => {
    if (!state || !stateKey) {
      return;
    }

    const denormalisedState = denormalisePersonalDataUK(state, values);

    try {
      setIsSubmitting(true);

      await navigateSignup({
        route: 'offers',
        updatedState: denormalisedState.data,
        beforeNavigate: () =>
          IwQueryClient.getOrCreate().invalidateQueries({
            queryKey: ['offers'],
          }),
      });
    } catch (error) {
      logger.error('Failed to submit personal details check', {
        error: error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipChangesClick = async () => {
    setIsSkipping(true);

    const newRoute: TSignupStepsUK = hasDocumentRequirements
      ? 'documents'
      : 'check-skipped';
    await navigateSignup({ route: newRoute });

    setIsSkipping(false);
  };

  return (
    <>
      <div className={personalDetailsStyles.container}>
        <h1 className={personalDetailsStyles.heading}>
          Let’s take another look
        </h1>
        {signupNoPGTest.splitTestBranch === 'INCLUDE' && (
          <div className={personalDetailsStyles.noPGContainer}>
            <p
              data-testid="no-pg-description"
              className={personalDetailsStyles.noPGTitle}
            >
              <strong>
                Loans with{' '}
                <span className={personalDetailsStyles.noPGHighlight}>
                  no personal guarantee
                </span>{' '}
                available
              </strong>
            </p>
            <p className={personalDetailsStyles.noPGDescription}>
              Complete your application to find out if you qualify.
            </p>
          </div>
        )}
        <PersonalDetailsCheckErrors
          hasDirectorDataRequirementError={hasDirectorDataRequirementError()}
          hasCreditDataRequirementError={hasCreditDataRequirementError()}
          hasMissingEquifaxToken={!hasEquifaxTokenOnCurrentAddress()}
        />
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={personalDetailsStyles.form}
          >
            <div className={personalDetailsStyles.formFieldContainer}>
              {hasDirectorDataRequirementError() && (
                <>
                  <div className={personalDetailsStyles.formField}>
                    <DirectorSearch
                      companyName={normalisedState.companyName}
                      companyNumber={normalisedState.companyNumber}
                      includeMiddleName={includeMiddleName}
                    />
                  </div>
                  <div className={personalDetailsStyles.formField}>
                    <Label className={personalDetailsStyles.formLabel}>
                      Date of birth
                    </Label>
                    <div className={personalDetailsStyles.dobContainer}>
                      <Input
                        {...register('dobDay')}
                        placeholder="DD"
                        data-testid="dob-day"
                        error={errors.dobDay?.message}
                        className="fs-mask"
                      />
                      <Input
                        {...register('dobMonth')}
                        placeholder="MM"
                        data-testid="dob-month"
                        error={errors.dobMonth?.message}
                        className="fs-mask"
                      />
                      <Input
                        {...register('dobYear')}
                        placeholder="YYYY"
                        data-testid="dob-year"
                        error={errors.dobYear?.message}
                        className="fs-mask"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className={personalDetailsStyles.formField}>
                <AddressInput allowMultipleAddresses />
              </div>
            </div>
            <div className={styles.buttonsContainer}>
              <Button
                className={styles.button}
                variant="secondary"
                loading={isSkipping || isRefetchingOffers}
                disabled={isSubmitting || isStateFetching}
                onClick={handleSkipChangesClick}
                type="button"
              >
                Skip changes and continue
              </Button>
              <Button
                className={styles.button}
                loading={isSubmitting || isStateFetching || isRefetchingOffers}
                disabled={isSkipping}
                type="submit"
              >
                Update
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
      {stateKey && <AccountCreatedModal stateKey={stateKey} />}
    </>
  );
};

export default PersonalDetailsCheck;
